import invariant from 'invariant';
import { useCallback, useEffect } from 'react';
import { sprintf } from 'sprintf-js';

import { ApiOrgId } from 'api/types/ApiTypedId';
import { Button } from 'components/Button/Button';
import { Text } from 'components/Text/Text';
import { useTemporaryStorage } from 'hooks/useTemporaryStorage';
import { ApiOrgType } from 'modules/listing/api/org/types/ApiOrgType';
import {
  useUserDashboardUserHasAppliedToListing,
  userDashboardCreateListingApplication,
  userDashboardDeleteListingApplication,
} from 'modules/userDashboard/zustand-stores/userDashboardCurrentListingApplicationsStore';
import { ListingType } from 'types/ListingType';
import { trackEvent } from 'utils/analytics/track/trackEvent';
import { LISTING_TYPE_HUMAN_NAME_MAP } from 'utils/constants/general/listingTypeHumanNameMap';
import { getFirstParam } from 'utils/url/getFirstParam';
import { showModal } from 'zustand-stores/modalStore';
import { useUser } from 'zustand-stores/userEnvironmentStore';

type Props = {
  listingId: string;
  listingType: ListingType;
  orgId?: ApiOrgId;
  orgType?: ApiOrgType;
  className?: string;
};

export function MarkAppliedMenuItem({
  listingId,
  listingType,
  orgId,
  orgType,
  className,
}: Props) {
  const { user } = useUser();
  const { userHasAppliedToListing } =
    useUserDashboardUserHasAppliedToListing(listingId);
  const [saveTemporaryStorage, temporaryStorage] = useTemporaryStorage(
    user?.emailVerified && typeof window !== 'undefined'
      ? getFirstParam(window.location, 'tmpId')
      : '',
  );

  const initialValues = temporaryStorage?.initialValues;

  useEffect(() => {
    if (
      initialValues &&
      listingId === initialValues.id &&
      listingType === 'JOB'
    ) {
      userDashboardCreateListingApplication({
        id: listingId,
        type: listingType,
        requestedAttachmentsHash: '',
      }).catch(() => {
        // noop
      });
    }
  }, [listingId, listingType, initialValues]);

  const onClick = useCallback(async () => {
    invariant(listingType === 'JOB', `listingType must be JOB`);
    if (!user) {
      trackEvent('Clicked: Marked Applied (Logged Out)', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
      });

      const values = {
        id: listingId,
        requestedAttachmentsHash: '',
        type: listingType,
      };
      const tmpId = await saveTemporaryStorage([
        {
          type: 'LISTING_APPLICATION_VALUES',
          data: values,
        },
      ]);

      const redirectTo = `${window.location.pathname}${
        window.location.search
      }${window.location.search ? '&' : '?'}tmpId=${tmpId || ''}`;

      await showModal('SIGNUP', {
        title: getText('Just One More Step!'),
        description: (
          <Text mb={4}>
            {sprintf(
              getText(
                'Create an account on Idealist in order to mark this %(listingType)s as applied.',
              ),
              {
                listingType: LISTING_TYPE_HUMAN_NAME_MAP[listingType],
              },
            )}
          </Text>
        ),
        redirectTo,
      });
    } else if (userHasAppliedToListing) {
      trackEvent('Clicked: Marked Applied', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
      });

      await userDashboardDeleteListingApplication(
        listingId,
        userHasAppliedToListing.applicationId,
      );
    } else {
      trackEvent('Clicked: Marked Not Applied', {
        listing_id: listingId,
        listing_type: listingType,
        org_id: orgId || null,
        org_type: orgType || null,
      });

      await userDashboardCreateListingApplication({
        id: listingId,
        type: listingType,
        requestedAttachmentsHash: '',
      });
    }
  }, [
    user,
    listingId,
    listingType,
    orgId,
    orgType,
    userHasAppliedToListing,
    saveTemporaryStorage,
  ]);

  if (userHasAppliedToListing) {
    return (
      <Button
        type="button"
        data-qa-id="mark-not-applied-button"
        variant="link-grey"
        onClick={onClick}
        className={className}
        size="small"
      >
        {getText('Mark As Not Applied')}
      </Button>
    );
  }

  return (
    <Button
      type="button"
      data-qa-id="mark-applied-button"
      variant="link-grey"
      onClick={onClick}
      className={className}
      size="small"
      icon={{ type: 'normalized', name: 'simple-check' }}
    >
      {getText('Mark As Applied')}
    </Button>
  );
}
